import { useEffect, useState } from 'react';
import type { PaymentOptions } from '../api.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * Recording that an invoice was paid.
 *
 * Deliberately not a copy of the document forms. A payment has no draft state
 * and nothing to preview: it is a short statement that money arrived, and the
 * only thing worth showing alongside it is how much the invoice still owes, so
 * nobody records more than is outstanding without noticing.
 *
 * One tab of the money screen, which owns the heading and the answer that
 * comes back. Settling an invoice, spending, receiving and transferring are
 * four ways of saying money moved, and a person who came here to record one
 * should not have to find out which of four pages they needed.
 */

function money(value: number, currency: string): string {
  return `${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

export function PaymentScreen({
  options,
  loading,
  busy,
  lang,
  justFiled,
  onLeaveUnpaid,
  t,
  onRecord,
}: {
  options: PaymentOptions | null;
  loading: boolean;
  busy: boolean;
  lang: Lang;
  /**
   * A document filed moments ago, arrived at straight from the form.
   *
   * Carries the id so it can be chosen the instant the options land —
   * the alternative is somebody hunting for the invoice they just made in
   * a list of everything outstanding, which is the step that gets put off
   * and then forgotten.
   */
  justFiled?: { id: string; number: string } | null;
  /** Leave it owing and go back. Only offered on a document just filed. */
  onLeaveUnpaid?: () => void;
  t: Translate;
  onRecord: (payment: {
    invoiceId: string;
    accountCode: string;
    method: string;
    date: string;
    amount: string;
    reference: string;
  }) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [invoiceId, setInvoiceId] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState('');

  const methods = options?.methods ?? [];

  /*
    Start on the company's usual method, once the options have loaded.

    Only while nothing is chosen — re-applying it after somebody has picked
    would quietly put their choice back, which is the sort of thing that gets
    a cash payment filed as a transfer.
  */
  useEffect(() => {
    if (method !== '' || methods.length === 0) return;
    setMethod(options?.defaultMethod ?? methods[0]!.code);
  }, [method, methods, options?.defaultMethod]);

  const invoices = options?.invoices ?? [];
  const accounts = options?.accounts ?? [];
  const chosen = invoices.find((i) => i.id === invoiceId) ?? null;
  const currency = chosen?.currency ?? options?.currency ?? '';

  /*
    The document somebody has just filed, chosen for them.

    Keyed on the id rather than run once, because the options arrive after
    this screen does and the invoice does not exist in the list until they
    do. Guarded on the field still being empty, so it cannot overwrite a
    different invoice somebody has since picked.
  */
  const filedId = justFiled?.id ?? '';
  useEffect(() => {
    if (filedId === '' || invoiceId !== '') return;
    const match = invoices.find((i) => i.id === filedId);
    if (!match) return;
    setInvoiceId(match.id);
    setAmount(String(match.amountDue));
  }, [filedId, invoiceId, invoices]);

  const typed = Number(amount.replace(/[^\d.-]/g, ''));
  const overpaying = chosen !== null && Number.isFinite(typed) && typed > chosen.amountDue;

  /*
    Paying part of an invoice has always worked — the field is prefilled with
    the full amount outstanding but nothing stops a smaller one, and Xero
    takes it. Nobody could tell, because a box already filled in reads as a
    box you are not meant to change.

    So the remainder is shown as it is typed. Saying "you may pay less" is a
    sentence people skip; showing what would still be owed is the same fact
    arriving at the moment it matters.
  */
  const partPaying =
    chosen !== null && Number.isFinite(typed) && typed > 0 && typed < chosen.amountDue;
  const remaining = chosen === null ? 0 : chosen.amountDue - typed;
  const ready = invoiceId !== '' && accountCode !== '' && method !== '' && amount.trim() !== '' && !busy;

  const field = {
    height: '36px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  return (
    <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Payment')}
        </div>

        <div style={{ padding: '16px' }}>
          <label style={{ display: 'block', marginBottom: '14px' }}>
            <span style={label}>{t('Invoice')}</span>
            <select
              value={invoiceId}
              onChange={(e) => {
                setInvoiceId(e.target.value);
                const picked = invoices.find((i) => i.id === e.target.value);
                // Offering the outstanding amount saves typing and makes paying
                // in full — which is most payments — a single choice.
                setAmount(picked ? String(picked.amountDue) : '');
              }}
              disabled={loading || invoices.length === 0}
              style={field}
            >
              <option value="">
                {loading
                  ? t('Reading from Xero…')
                  : invoices.length === 0
                    ? t('No approved invoices are awaiting payment')
                    : t('Choose…')}
              </option>
              {invoices.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {[
                    invoice.number || invoice.reference || '—',
                    invoice.contact,
                    money(invoice.amountDue, invoice.currency),
                    invoice.direction === 'out' ? t('to pay') : t('owed to you'),
                  ]
                    .filter(Boolean)
                    .join('  ·  ')}
                </option>
              ))}
            </select>
          </label>

          {chosen && (
            <div
              style={{
                background: theme.surfaceMuted,
                border: `1px solid ${theme.border}`,
                borderRadius: theme.radiusControl,
                padding: '10px 12px',
                marginBottom: '14px',
                fontSize: '12.5px',
                lineHeight: 1.7,
              }}
            >
              <div>
                {t('Still outstanding')}:{' '}
                <span style={{ fontFamily: theme.fontMono, fontWeight: 600 }}>
                  {money(chosen.amountDue, currency)}
                </span>
                <span style={{ color: theme.textMuted }}>
                  {' '}
                  {t('of')} {money(chosen.total, currency)}
                </span>
              </div>
              {chosen.due && (
                <div style={{ color: theme.textMuted }}>
                  {t('Due')} {chosen.due}
                </div>
              )}
            </div>
          )}

          {/*
            Full or part, said as a choice rather than left to be discovered.

            Paying part of an invoice always worked — the amount box is
            prefilled with everything outstanding and nothing stopped a
            smaller figure — but a box already filled in reads as a box you
            are not meant to change. Naming the two cases makes the second
            one visible; picking Full simply puts the whole balance back.
          */}
          {chosen && (
            <div style={{ marginBottom: '14px' }}>
              <span style={label}>{t('Payment type')}</span>
              <div
                style={{
                  display: 'inline-flex',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusPill,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                {([true, false] as const).map((full) => {
                  const picked = full ? !partPaying : partPaying;
                  return (
                    <button
                      key={String(full)}
                      type="button"
                      onClick={() => setAmount(full ? String(chosen.amountDue) : '')}
                      style={{
                        border: 0,
                        padding: '7px 16px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: picked ? theme.nav : 'transparent',
                        color: picked ? '#fff' : theme.textMuted,
                      }}
                    >
                      {full ? t('Full payment') : t('Part payment')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
            <label style={{ flex: '1 1 220px', minWidth: 0 }}>
              <span style={label}>{t('Paid into')}</span>
              <select
                value={accountCode}
                onChange={(e) => setAccountCode(e.target.value)}
                disabled={loading || accounts.length === 0}
                style={field}
              >
                <option value="">
                  {accounts.length === 0 && !loading
                    ? t('No account in Xero can receive payments')
                    : t('Choose…')}
                </option>
                {accounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} · {t(account.name)}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ flex: '0 1 170px', minWidth: 0 }}>
              <span style={label}>{t('Date paid')}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} />
            </label>

            <label style={{ flex: '0 1 170px', minWidth: 0 }}>
              <span style={label}>{t('Amount')}</span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                dir="ltr"
                style={{
                  ...field,
                  fontFamily: theme.fontMono,
                  borderColor: overpaying ? theme.bad : theme.borderStrong,
                }}
              />
            </label>
          </div>

          {/*
            What this leaves behind, as three lines that add up.

            The sentence this replaces said the same thing and said it only
            while part-paying. Shown always, because "nothing left owing"
            is as worth reading as a remainder, and a figure that appears
            only sometimes is one nobody learns to look for.
          */}
          {chosen && Number.isFinite(typed) && (
            <div
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: theme.radiusControl,
                padding: '11px 13px',
                marginBottom: '12px',
                fontSize: '12.5px',
                display: 'grid',
                gap: '6px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <span style={{ color: theme.textMuted }}>{t('Outstanding before this')}</span>
                <span style={{ fontFamily: theme.fontMono }} dir="ltr">
                  {money(chosen.amountDue, currency)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <span style={{ color: theme.textMuted }}>{t('This payment')}</span>
                <span style={{ fontFamily: theme.fontMono }} dir="ltr">
                  {money(typed > 0 ? typed : 0, currency)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  borderTop: `1px solid ${theme.dividerSoft}`,
                  paddingTop: '6px',
                  fontWeight: 700,
                }}
              >
                <span>{t('Still owing after')}</span>
                <span
                  style={{ fontFamily: theme.fontMono, color: remaining > 0 ? theme.text : theme.goodDeep }}
                  dir="ltr"
                >
                  {money(remaining > 0 ? remaining : 0, currency)}
                </span>
              </div>
            </div>
          )}

          {overpaying && (
            <div style={{ fontSize: '12.5px', color: theme.badDeep, marginBottom: '12px', fontWeight: 600 }}>
              {t('That is more than this invoice still owes.')}{' '}
              <span style={{ fontWeight: 400 }}>
                {t('Xero will refuse it, or record a credit you did not mean to create.')}
              </span>
            </div>
          )}

          <label style={{ display: 'block', marginBottom: '16px' }}>
            <span style={label}>
              {t('Reference')}{' '}
              <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
            </span>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('Bank transfer number, cheque number')}
              style={field}
            />
          </label>

          {/*
            How the money arrived. Xero has no field for this, so it is kept
            here and prepended to the reference Xero is given — which is why
            the line below shows exactly what the ledger will end up saying,
            rather than leaving somebody to find out afterwards.
          */}
          <div style={{ display: 'grid', gap: '7px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>{t('How it was paid')}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {methods.map((option) => {
                const picked = option.code === method;
                return (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => setMethod(option.code)}
                    style={{
                      background: picked ? theme.nav : 'transparent',
                      color: picked ? '#fff' : theme.text,
                      border: `1px solid ${picked ? theme.nav : theme.borderStrong}`,
                      borderRadius: theme.radiusControl,
                      padding: '8px 14px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {lang === 'ar' ? option.ar : option.en}
                  </button>
                );
              })}
            </div>

            {methods.length === 0 && !loading && (
              <span style={{ fontSize: '12px', color: theme.textMuted }}>
                {t('No ways of paying have been set for this company. Admin → Payment methods.')}
              </span>
            )}

            {method !== '' && (
              <span style={{ fontSize: '11.5px', color: theme.textMuted }}>
                {t('Xero will show this as')}{' '}
                <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                  {[methods.find((m) => m.code === method)?.en, reference.trim()]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              disabled={!ready}
              onClick={() => onRecord({ invoiceId, accountCode, method, date, amount, reference })}
              style={{
                height: '42px',
                minWidth: '220px',
                background: ready ? theme.nav : theme.borderStrong,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                fontSize: '14px',
                fontWeight: 600,
                cursor: ready ? 'pointer' : 'default',
              }}
            >
              {busy ? t('Recording…') : t('Record the payment')}
            </button>

            {/*
              Leaving it owing is an answer, not an escape.

              Offered only on a document just filed, because that is the
              only moment the question is being asked. Everywhere else
              somebody came to this screen on purpose and already has the
              sidebar to leave by.

              It does nothing to Xero. The invoice is filed and owing,
              which is what it already was a second ago.
            */}
            {justFiled && onLeaveUnpaid && (
              <button
                type="button"
                onClick={onLeaveUnpaid}
                disabled={busy}
                style={{
                  height: '42px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '0 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.text,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {t('Leave it unpaid')}
              </button>
            )}
          </div>

          <div style={{ marginTop: '9px', fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.6 }}>
            {t('This is recorded in Xero straight away. A payment has no draft to approve.')}
          </div>
        </div>
    </Card>
  );
}
